import { useMemo, useState } from "react";
import { App, Input, Table } from "antd";
import {
  BankOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import type { Company, CompanyPayload } from "../../../../api/companies";
import {
  useCreateCompany,
  useDeleteCompany,
  useUpdateCompany,
} from "../../../../hooks/useCompanies";
import DeleteConfirmModal from "../DeleteConfirmModal";
import CompanyModal from "./CompanyModal";
import { getApiErrorMessage } from "../../../../utils/translations";
import {
  ConfigPanel,
  PanelAddButton,
  PanelIconAction,
} from "../panel/ConfigPanel";
import { PANEL_ACCENTS } from "../panel/panelAccents";

const R = {
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  ok: "var(--color-ok-deep)",
};

// Една секция на страницата → основният оранжев акцент.
const accent = PANEL_ACCENTS.amber;

interface Props {
  companies: Company[];
  isNarrow?: boolean;
}

const CompaniesTable = ({ companies, isNarrow = false }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();
  const deleteMutation = useDeleteCompany();

  const [searchText, setSearchText] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Company | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);

  const filtered = useMemo(() => {
    if (!searchText.trim()) return companies;
    const q = searchText.toLowerCase();
    return companies.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.object_name.toLowerCase().includes(q) ||
        c.eik.includes(q),
    );
  }, [companies, searchText]);

  // BE-то може да върне detail-а като стринг (legacy) или като
  // `{bg: "...", en: "..."}` dict. Помощникът избира подходящия език.
  const extractError = (error: unknown, fallback: string): string =>
    getApiErrorMessage(error, fallback);

  const handleCreateSubmit = async (data: CompanyPayload) => {
    try {
      await createMutation.mutateAsync(data);
      message.success(t("companiesConfig.createSuccess"));
      setCreateOpen(false);
    } catch (error: unknown) {
      message.error(extractError(error, t("companiesConfig.createError")));
      throw error;
    }
  };

  const handleEditSubmit = async (data: CompanyPayload) => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data });
      message.success(t("companiesConfig.editSuccess"));
      setEditTarget(null);
    } catch (error: unknown) {
      message.error(extractError(error, t("companiesConfig.editError")));
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      message.success(t("companiesConfig.deleteSuccess"));
      setDeleteTarget(null);
    } catch (error: unknown) {
      message.error(extractError(error, t("companiesConfig.deleteError")));
      throw error;
    }
  };

  const totalCount = filtered.length;

  const columns: ColumnsType<Company> = [
    {
      title: t("companiesConfig.colName"),
      key: "name",
      // Без хардкод width — взима leftover. Сетваме обаче колоните отдясно
      // по-широки, за да не доминира Името непропорционално.
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_: unknown, r: Company) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: R.ink,
              letterSpacing: -0.1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {r.is_default && (
              <CheckCircleFilled
                style={{ color: R.ok, fontSize: 12 }}
                title={t("companiesConfig.badgeDefault")}
              />
            )}
            {r.name}
          </span>
          <span style={{ fontSize: 11.5, color: R.textMuted }}>
            {r.object_name}
          </span>
        </div>
      ),
    },
    {
      title: t("companiesConfig.colEik"),
      key: "eik",
      width: 200,
      render: (_: unknown, r: Company) => (
        <span
          style={{
            fontFamily:
              '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
            fontSize: 12.5,
            color: R.textSoft,
          }}
        >
          {r.eik}
        </span>
      ),
    },
    {
      title: t("companiesConfig.colVat"),
      key: "vat",
      width: 220,
      responsive: ["md"],
      render: (_: unknown, r: Company) => (
        <span style={{ fontSize: 12.5, color: R.textSoft }}>
          {r.vat_number || "—"}
        </span>
      ),
    },
    {
      title: t("companiesConfig.colActions"),
      key: "actions",
      width: 160,
      align: "right",
      render: (_: unknown, r: Company) => (
        <div style={{ display: "inline-flex", gap: 6 }}>
          <PanelIconAction
            onClick={() => setEditTarget(r)}
            label={t("common.edit", "Edit")}
            accent={accent}
          >
            <EditOutlined />
          </PanelIconAction>
          <PanelIconAction
            onClick={() => setDeleteTarget(r)}
            label={t("common.delete", "Delete")}
            danger
            accent={accent}
          >
            <DeleteOutlined />
          </PanelIconAction>
        </div>
      ),
    },
  ];

  return (
    <>
      <ConfigPanel
        scope="co-list"
        accent={accent}
        icon={<BankOutlined />}
        kicker={t("companiesConfig.listKicker")}
        title={t("companiesConfig.title")}
        count={totalCount}
        isNarrow={isNarrow}
        isEmpty={filtered.length === 0}
        emptyText={t("companiesConfig.noCompanies")}
        toolbar={
          <>
            <Input
              placeholder={t("companiesConfig.searchPlaceholder")}
              prefix={<SearchOutlined style={{ color: R.textMuted }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: isNarrow ? "100%" : 260 }}
            />
            <PanelAddButton
              onClick={() => setCreateOpen(true)}
              label={t("companiesConfig.addCompany")}
              accent={accent}
              isNarrow={isNarrow}
            />
          </>
        }
      >
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          pagination={false}
          scroll={{ x: 720 }}
          size="small"
        />
      </ConfigPanel>

      <CompanyModal
        open={createOpen}
        mode="create"
        onSubmit={handleCreateSubmit}
        onCancel={() => setCreateOpen(false)}
      />
      <CompanyModal
        open={!!editTarget}
        mode="edit"
        company={editTarget}
        onSubmit={handleEditSubmit}
        onCancel={() => setEditTarget(null)}
      />

      <DeleteConfirmModal
        open={!!deleteTarget}
        title={t("companiesConfig.deleteConfirmTitle")}
        description={t("companiesConfig.deleteConfirmContent", {
          name: deleteTarget?.name ?? "",
        })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default CompaniesTable;
